/**
 * Signature Service
 *
 * Handles digital signature capture and storage for work approvals,
 * material receipts, and other documentation requiring signatures.
 *
 * DB table: project_signatures. The live table shape (reconciled in
 * #305, see supabase/migrations/20260705150000_fix_project_signatures_phantom_columns.sql)
 * differs from what migration 027 originally intended:
 *   id, project_id, signature_type, signer_name, signer_role,
 *   signer_email, signature_data, signed_by, ip_address, device_info,
 *   notes, created_at, task_id, latitude, longitude, signer_id_number
 *
 * Notably there is no signature_image_url / captured_by — the base64
 * signature image is stored directly in signature_data (TEXT), and the
 * capturing user is signed_by (matches mcp-server/src/tools/signatureTools.js,
 * which has always used the correct live columns).
 *
 * @module services/signatureService
 */

import { supabase } from './supabase';
import {
  SIGNATURE_TYPE,
  STORAGE_BUCKETS,
  canCaptureSignatureType,
  signatureDenialMessage,
} from '../utils/constants';

/**
 * Rol del usuario logueado, para la compuerta de firmas (#525).
 *
 * La ley es la RLS (supabase/migrations/20260729070000_techo_firmas.sql); esto
 * existe para que el técnico lea un motivo en castellano en vez de un
 * "new row violates row-level security policy". No se cachea a propósito: es
 * una lectura por captura de firma, que ocurre una vez por visita.
 */
const getCallerRole = async (userId) => {
  const { data } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', userId)
    .single();
  return data?.role || null;
};

/**
 * Capture and save a signature
 * @param {Object} data - Signature data
 * @param {string} data.project_id - Project ID
 * @param {string} data.task_id - Task ID (optional)
 * @param {string} data.signature_type - Type of signature
 * @param {string} data.signer_name - Name of the person signing
 * @param {string} data.signer_role - Role/title of the signer (optional)
 * @param {string} data.signer_id_number - ID number / cedula (optional)
 * @param {string} data.signature_data - Base64 encoded signature image
 * @param {Object} data.location - GPS coordinates (optional)
 * @param {string} data.notes - Additional notes (optional)
 * @returns {Promise<{data, error}>}
 */
export const captureSignature = async (data) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Validate required fields
    if (!data.project_id) throw new Error('Se requiere el proyecto');
    if (!data.signature_type) throw new Error('Se requiere el tipo de firma');
    if (!data.signer_name) throw new Error('Se requiere el nombre del firmante');
    if (!data.signature_data) throw new Error('Se requiere la imagen de la firma');

    // Compuerta de rol (#525) — paridad con
    // mcp-server/src/tools/signatureTools.js. El ámbito por proyecto lo
    // resuelve la RLS con has_project_stake(); acá sólo va lo que se decide
    // con rol + tipo.
    const callerRole = await getCallerRole(user.id);
    if (!canCaptureSignatureType(callerRole, data.signature_type)) {
      throw new Error(signatureDenialMessage(data.signature_type));
    }

    // The live signature_data column stores the base64-encoded signature
    // image inline (TEXT) — no separate storage upload/URL, matching
    // mcp-server/src/tools/signatureTools.js.
    const { data: signature, error } = await supabase
      .from('project_signatures')
      .insert({
        project_id: data.project_id,
        task_id: data.task_id || null,
        signature_type: data.signature_type,
        signer_name: data.signer_name,
        signer_role: data.signer_role || null,
        // `signer_id` is a deprecated alias kept only as a back-compat net for
        // any caller still using the old name; the technician portal now sends
        // signer_id_number directly (#311). Prefer signer_id_number.
        signer_id_number: data.signer_id_number || data.signer_id || null,
        signature_data: data.signature_data,
        latitude: data.location?.latitude || null,
        longitude: data.location?.longitude || null,
        notes: data.notes || null,
        signed_by: user.id,
      })
      .select(`
        *,
        project:projects(id, project_number, title),
        task:tasks(id, title, task_number),
        captured_by_profile:profiles!signed_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data: signature, error: null };
  } catch (error) {
    console.error('[SIGNATURE] Capture signature error:', error);
    // El techo rebota con 42501 y un texto que no orienta a nadie. Traducirlo
    // es la mitad útil de la compuerta.
    if (error?.code === '42501') {
      return {
        data: null,
        error: new Error(
          'Sólo podés capturar firmas en proyectos donde participás '
          + '(tarea asignada, cotización que liderás, o proyecto propio).'
        ),
      };
    }
    return { data: null, error };
  }
};

/**
 * Get signatures for a project
 * @param {string} projectId - Project ID
 * @param {Object} filters - Filter options
 * @param {string} filters.type - Filter by signature type
 * @param {string} filters.dateFrom - Start date
 * @param {string} filters.dateTo - End date
 * @returns {Promise<{data, error}>}
 */
export const getSignatures = async (projectId, filters = {}) => {
  try {
    let query = supabase
      .from('project_signatures')
      .select(`
        *,
        task:tasks(id, title, task_number),
        captured_by_profile:profiles!signed_by(id, full_name)
      `)
      .eq('project_id', projectId);

    if (filters.type) {
      query = query.eq('signature_type', filters.type);
    }

    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[SIGNATURE] Get signatures error:', error);
    return { data: null, error };
  }
};

/**
 * Get signatures filtered by type
 * @param {string} projectId - Project ID
 * @param {string} type - Signature type
 * @returns {Promise<{data, error}>}
 */
export const getSignaturesByType = async (projectId, type) => {
  try {
    // Validate signature type
    const validTypes = Object.values(SIGNATURE_TYPE);
    if (!validTypes.includes(type)) {
      throw new Error('Tipo de firma no valido');
    }

    const { data, error } = await supabase
      .from('project_signatures')
      .select(`
        *,
        task:tasks(id, title, task_number),
        captured_by_profile:profiles!signed_by(id, full_name)
      `)
      .eq('project_id', projectId)
      .eq('signature_type', type)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[SIGNATURE] Get by type error:', error);
    return { data: null, error };
  }
};

/**
 * Get a single signature by ID
 * @param {string} signatureId - Signature ID
 * @returns {Promise<{data, error}>}
 */
export const getSignature = async (signatureId) => {
  try {
    const { data, error } = await supabase
      .from('project_signatures')
      .select(`
        *,
        project:projects(id, project_number, title, client:clients!projects_client_id_fkey(company_name, primary_contact_name)),
        task:tasks(id, title, task_number),
        captured_by_profile:profiles!signed_by(id, full_name)
      `)
      .eq('id', signatureId)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[SIGNATURE] Get signature error:', error);
    return { data: null, error };
  }
};

/**
 * Upload signature image to storage
 * @param {string} base64Data - Base64 encoded image data
 * @param {string} projectId - Project ID for organizing files
 * @returns {Promise<{url, path, error}>}
 */
export const uploadSignatureImage = async (base64Data, projectId) => {
  try {
    // Remove data URL prefix if present
    const base64Content = base64Data.replace(/^data:image\/\w+;base64,/, '');

    // Convert base64 to blob
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/png' });

    // Generate unique filename
    const fileName = `signatures/${projectId}/${Date.now()}_${Math.random().toString(36).substring(7)}.png`;

    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
      .upload(fileName, blob, {
        contentType: 'image/png',
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    // Get signed URL (1-hour expiry, see CLAUDE.md storage policy)
    const { data: urlData, error: urlError } = await supabase.storage
      .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
      .createSignedUrl(data.path, 3600);

    if (urlError) {
      // Fallback to public URL
      const { data: { publicUrl } } = supabase.storage
        .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
        .getPublicUrl(data.path);
      return { url: publicUrl, path: data.path, error: null };
    }

    return { url: urlData.signedUrl, path: data.path, error: null };
  } catch (error) {
    console.error('[SIGNATURE] Upload image error:', error);
    return { url: null, path: null, error };
  }
};

/**
 * Get signatures for a specific task
 * @param {string} taskId - Task ID
 * @returns {Promise<{data, error}>}
 */
export const getSignaturesByTask = async (taskId) => {
  try {
    const { data, error } = await supabase
      .from('project_signatures')
      .select(`
        *,
        project:projects(id, project_number, title),
        captured_by_profile:profiles!signed_by(id, full_name)
      `)
      .eq('task_id', taskId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[SIGNATURE] Get by task error:', error);
    return { data: null, error };
  }
};

/**
 * Verify signature exists for a project/type combination
 * @param {string} projectId - Project ID
 * @param {string} type - Signature type
 * @returns {Promise<{exists, count, error}>}
 */
export const verifySignatureExists = async (projectId, type) => {
  try {
    const { count, error } = await supabase
      .from('project_signatures')
      .select('id', { count: 'exact' })
      .eq('project_id', projectId)
      .eq('signature_type', type);

    if (error) throw error;

    return { exists: count > 0, count, error: null };
  } catch (error) {
    console.error('[SIGNATURE] Verify signature error:', error);
    return { exists: false, count: 0, error };
  }
};

/**
 * Get signature statistics for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<{data, error}>}
 */
export const getSignatureStats = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from('project_signatures')
      .select('signature_type, created_at')
      .eq('project_id', projectId);

    if (error) throw error;

    const stats = {
      total: data.length,
      byType: {},
    };

    // Count by type
    Object.values(SIGNATURE_TYPE).forEach(type => {
      const typeSignatures = data.filter(s => s.signature_type === type);
      stats.byType[type] = {
        count: typeSignatures.length,
        lastCaptured: typeSignatures.length > 0
          ? typeSignatures.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0].created_at
          : null,
      };
    });

    return { data: stats, error: null };
  } catch (error) {
    console.error('[SIGNATURE] Get stats error:', error);
    return { data: null, error };
  }
};

/**
 * Delete a signature (admin only)
 * @param {string} signatureId - Signature ID
 * @returns {Promise<{success, error}>}
 */
export const deleteSignature = async (signatureId) => {
  try {
    // signature_data is stored inline (base64 TEXT column) — no separate
    // storage object to clean up, just remove the row.
    const { error } = await supabase
      .from('project_signatures')
      .delete()
      .eq('id', signatureId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('[SIGNATURE] Delete signature error:', error);
    return { success: false, error };
  }
};

/**
 * Get recent signatures (across all projects)
 * @param {number} limit - Maximum number to return
 * @returns {Promise<{data, error}>}
 */
export const getRecentSignatures = async (limit = 10) => {
  try {
    const { data, error } = await supabase
      .from('project_signatures')
      .select(`
        *,
        project:projects(id, project_number, title),
        captured_by_profile:profiles!signed_by(id, full_name)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[SIGNATURE] Get recent signatures error:', error);
    return { data: null, error };
  }
};

const signatureService = {
  captureSignature,
  getSignatures,
  getSignaturesByType,
  getSignature,
  uploadSignatureImage,
  getSignaturesByTask,
  verifySignatureExists,
  getSignatureStats,
  deleteSignature,
  getRecentSignatures,
};

export default signatureService;
